import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"

import type { GraphActivity } from "./GraphActivity"

/*
 * sort graphActivities so bolus activities are displayed in the available
 * rows
 *
 * this function returns an array of the same length as graphActivities, even
 * though the expectation is that any activities after the availableRows will
 * not be rendered
 */
export function prioritizeBolusActivity(
  graphActivities: GraphActivity[], // sorted by descending startedAt
  availableRows: number,
): GraphActivity[] {
  const bolusActivities: GraphActivity[] = []
  const nonBolusActivities: GraphActivity[] = []

  // sort graph activities into bolus and non-bolus arrays
  graphActivities.forEach((graphActivity) => {
    if (graphActivity.activityType === ActivityType.Bolus) {
      bolusActivities.push(graphActivity)
    } else {
      nonBolusActivities.push(graphActivity)
    }
  })

  let bolusIndex = 0
  let nonBolusIndex = 0
  const resortedGraphActivities: GraphActivity[] = []

  while (resortedGraphActivities.length < graphActivities.length) {
    const nextBolusActivity = bolusActivities[bolusIndex]
    const nextNonBolusActivity = nonBolusActivities[nonBolusIndex]

    if (!nextBolusActivity && !nextNonBolusActivity) {
      // when no activities are left exit the loop (this should never happen)
      break
    } else if (!nextBolusActivity) {
      // when there are only non-bolus activities left
      resortedGraphActivities.push(nextNonBolusActivity)
      nonBolusIndex++
    } else if (!nextNonBolusActivity) {
      // when there are only bolus activities left
      resortedGraphActivities.push(nextBolusActivity)
      bolusIndex++
    } else {
      // when there are both bolus and non-bolus activities left
      const availableSpacesLeft =
        availableRows - resortedGraphActivities.length
      const bolusActivitiesLeft = bolusActivities.length - bolusIndex

      if (availableSpacesLeft <= bolusActivitiesLeft) {
        // prioritize bolus activities for the remaining available space
        resortedGraphActivities.push(nextBolusActivity)
        bolusIndex++
      } else if (
        dayjs(nextBolusActivity.startedAt).isAfter(
          nextNonBolusActivity.startedAt,
        )
      ) {
        resortedGraphActivities.push(nextBolusActivity)
        bolusIndex++
      } else {
        resortedGraphActivities.push(nextNonBolusActivity)
        nonBolusIndex++
      }
    }
  }

  return resortedGraphActivities
}
